import { memo, useMemo, useState } from "react";
import { QTY_FAVORITE_CHOICES, sanitizeQtyFavorites, toggleQtyFavorite, useSettings } from "@helm/core";
import { settingsSync } from "../session";
import { usePopover } from "./Popover";

/**
 * The user's favourite order quantities (synced settings `qty_favorites`, ascending). One source
 * for the DOM quantity chips and the chart order bar's quick quantities. Subscribes to the
 * settings only, never to market data, so it does not re-render on ticks.
 */
export function useQtyFavorites(): readonly number[] {
  const stored = useSettings((s) => s.settings.qty_favorites);
  return useMemo(() => sanitizeQtyFavorites(stored), [stored]);
}

/**
 * Favourite quantity chips plus a "▾" list of 1–10 with star toggles (owner 2026-10-04): a star
 * stars / unstars a quantity (synced to every device); a number sets the quantity and closes the
 * list. Unstarring the last favourite is refused with a one-line reason. Quantities above 10 are
 * still typed in the stepper.
 */
export const QtyFavorites = memo(function QtyFavorites({
  value,
  onPick,
  max,
}: {
  value: number;
  onPick: (q: number) => void;
  max: number;
}): React.JSX.Element {
  const favs = useQtyFavorites();
  const list = usePopover();
  const [notice, setNotice] = useState<string | null>(null);
  const over = (q: number): boolean => q > max;
  const titleOf = (q: number): string =>
    over(q) ? `Above the max order quantity (${String(max)})` : `Quantity ${String(q)}`;
  const toggle = (q: number): void => {
    const r = toggleQtyFavorite(favs, q);
    if (!r.ok) {
      setNotice(r.reason);
      return;
    }
    setNotice(null);
    void settingsSync.update({ qty_favorites: r.favorites });
  };
  return (
    <div className="qty-favs">
      <div className="qty-chips" role="group" aria-label="Favourite quantities">
        {favs.map((q) => (
          <button
            key={q}
            type="button"
            className={q === value ? "qty-chip active" : "qty-chip"}
            aria-pressed={q === value}
            disabled={over(q)}
            title={titleOf(q)}
            onClick={() => {
              onPick(q);
            }}
          >
            {q}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="qty-fav-more"
        aria-haspopup="dialog"
        aria-label="Quantities 1–10 and favourites"
        title="Quantities 1–10: click a number to use it, a star to show it as a chip"
        {...list.triggerProps}
        onClick={() => {
          setNotice(null);
          list.triggerProps.onClick();
        }}
      >
        ▾
      </button>
      {list.open && (
        <div className="qty-fav-list" role="dialog" aria-label="Quantity favourites" {...list.panelProps}>
          <div className="qty-fav-grid">
            {QTY_FAVORITE_CHOICES.map((q) => {
              const starred = favs.includes(q);
              return (
                <span key={q} className="qty-fav-item">
                  <button
                    type="button"
                    className={q === value ? "qty-fav-num active" : "qty-fav-num"}
                    aria-pressed={q === value}
                    disabled={over(q)}
                    title={titleOf(q)}
                    onClick={() => {
                      onPick(q);
                      list.setOpen(false);
                    }}
                  >
                    {q}
                  </button>
                  <button
                    type="button"
                    className={starred ? "qty-fav-star starred" : "qty-fav-star"}
                    aria-pressed={starred}
                    aria-label={starred ? `Unstar quantity ${String(q)}` : `Star quantity ${String(q)}`}
                    title={starred ? "Remove from the favourite chips" : "Show as a favourite chip"}
                    onClick={() => {
                      toggle(q);
                    }}
                  >
                    {starred ? "★" : "☆"}
                  </button>
                </span>
              );
            })}
          </div>
          {notice !== null && (
            <div className="qty-fav-notice" role="status">
              {notice}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
